<script setup lang="ts">
import { GEO_HUB_MIN_CITY } from "#shared/utils/geoHubThresholds";
import { nomDepuisCleVille } from "#shared/utils/villeAnnuaire";
import { lieuSchemaDepuisMorceaux, type LieuPublicLu } from "#shared/utils/lieuPublic";
import { formatDateAssociation, fuseauDe, isoAvecDecalage } from "~/utils/heureAssociation";

const { t } = useI18n({ useScope: "local" });

/**
 * Page programmatique SEO : événements associatifs par ville (longue traîne).
 * Ex : /evenements/marseille — ranke sur "billetterie marseille",
 * "événement associatif marseille".
 *
 * Karim 2026-06-15 (SEO sprint v4).
 */
const config = useRuntimeConfig();
const apiUrl = config.public.apiBaseUrl;
const assetsUrl = `${apiUrl}/api/assets`;

const route = useRoute();
const villeSlug = (route.params as Record<string, string | string[] | undefined>).ville as string;

// Lieu structuré exposé par l'API (`CHAMPS_LIEU_PUBLICS`), facultatif.
interface EventWithCity extends LieuPublicLu {
  id: number;
  title: string;
  slug: string;
  cover_image: string | null;
  start_date: string | null;
  end_date: string | null;
  location_name: string | null;
  location_city: string | null;
  organization_id: number;
  organization_name: string;
  organization_slug: string;
  organization_logo: string | null;
  city_slug: string;
  city_name: string;
}

const { data, pending } = await useAsyncData(
  `events-city-${villeSlug}`,
  async () => {
    try {
      return await $fetch<{
        cities: Array<{ slug: string; name: string; count: number }>;
        events: EventWithCity[];
        cityName: string | null;
        redirection?: string | null;
      }>(`/api/events/by-city?city=${villeSlug}`);
    } catch {
      return { cities: [], events: [], cityName: null, redirection: null };
    }
  },
  { server: true, lazy: false },
);

// Ancienne clé de ville (lot 4 : suffixe d'outre-mer ajouté, commune
// renommée par la BAN) : 301 vers la nouvelle clé, même hub.
if (data.value?.redirection) {
  await navigateTo(`/evenements/${data.value.redirection}`, { redirectCode: 301 });
}

const events = computed(() => data.value?.events || []);
const cityName = computed(() => {
  if (data.value?.cityName) return data.value.cityName;
  return nomDepuisCleVille(villeSlug);
});

// Balise canonique = l'URL RÉELLE de la page. Elle pointait vers
// /billetterie/{ville}, une adresse qui n'existe pas : Google recevait une
// canonique en 404 et ignorait la page.
const canonicalUrl = `https://donoor.org/evenements/${villeSlug}`;

const seoTitle = computed(
  () => t('seo.titre', { ville: cityName.value }),
);
const seoDescription = computed(
  () =>
    events.value.length > 1
      ? t('seo.description.plusieurs', { nombre: events.value.length, ville: cityName.value })
      : t('seo.description.un', { nombre: events.value.length, ville: cityName.value }),
);

const shouldNoindex = computed(
  () => !pending.value && events.value.length < GEO_HUB_MIN_CITY,
);
// robots cohérent avec l'indexabilité globale (staging = noindex) + noindex page si contenu mince.
const robotsContent = useRobotsContent(shouldNoindex);

// Jour de l'événement dans le fuseau de l'ASSOCIATION (`event.timezone`,
// Paris par défaut) ; chaîne naïve de la base = UTC (lot 2, 2026-09-25).
const formatEventDate = (iso: string | null, fuseau?: string | null): string =>
  formatDateAssociation(iso, fuseau, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogImage: () => `https://donoor.org/api/og/recherche?type=evenements&count=${events.value.length}`,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogUrl: canonicalUrl,
  ogLocale: "fr_FR",
  twitterCard: "summary_large_image",
  robots: () => robotsContent.value,
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
  script: computed(() => [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoTitle.value,
        description: seoDescription.value,
        url: canonicalUrl,
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: t('breadcrumb.accueil'), item: "https://donoor.org" },
            { "@type": "ListItem", position: 2, name: t('breadcrumb.evenements'), item: "https://donoor.org/recherche/billetterie" },
            { "@type": "ListItem", position: 3, name: cityName.value, item: canonicalUrl },
          ],
        },
        ...(events.value.length > 0 && {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: events.value.length,
            itemListElement: events.value.slice(0, 20).map((e, i) => {
              // Lieu (§10) : `Place` avec son `PostalAddress` (code postal,
              // ville, territoire, pays réel) et son point, depuis les
              // morceaux exposés — plus un simple nom.
              const lieu = lieuSchemaDepuisMorceaux(e);
              return {
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "Event",
                  name: e.title,
                  url: `https://donoor.org/b/${e.slug}`,
                  ...(e.start_date ? { startDate: isoAvecDecalage(e.start_date, fuseauDe(e)) || e.start_date } : {}),
                  ...(lieu ? { location: lieu } : {}),
                  organizer: { "@type": "Organization", name: e.organization_name },
                },
              };
            }),
          },
        }),
      }),
    },
  ]),
});
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Hero unifié (même UX que /evenements) avec ville pré-filtrée -->
    <RechercheUnifiedHero
      active-type="evenements"
      :h1="t('hero.titre', { ville: cityName })"
      :intro="events.length > 1 ? t('hero.intro.plusieurs', { nombre: events.length, ville: cityName }) : t('hero.intro.un', { nombre: events.length, ville: cityName })"
      :ville-slug="villeSlug"
      :ville-name="cityName"
    />

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
      <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div v-for="i in 6" :key="i" class="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
          <div class="h-44 bg-gray-200" />
          <div class="p-4 space-y-2">
            <div class="h-4 w-3/4 bg-gray-200 rounded" />
            <div class="h-3 w-1/2 bg-gray-100 rounded" />
          </div>
        </div>
      </div>

      <div v-else-if="events.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <NuxtLink
          v-for="e in events"
          :key="e.id"
          :to="`/b/${e.slug}`"
          :aria-label="t('carte.reserver', { titre: e.title, ville: cityName })"
          class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 no-underline"
        >
          <div class="relative h-44 overflow-hidden bg-gray-100">
            <img
              v-if="e.cover_image"
              :src="`${assetsUrl}/${e.cover_image}?width=600&height=400&fit=cover&format=webp&quality=80`"
              :alt="t('carte.imageAlt', { titre: e.title })"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
              <UIcon name="i-lucide-calendar" class="size-10" />
            </div>
            <div v-if="e.start_date" class="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-lg px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm">
              {{ formatEventDate(e.start_date, fuseauDe(e)) }}
            </div>
          </div>
          <div class="p-4">
            <div class="flex items-center gap-2 mb-2">
              <img
                v-if="e.organization_logo"
                :src="`${assetsUrl}/${e.organization_logo}?width=40&height=40&fit=contain&format=webp&quality=80`"
                :alt="t('carte.logoAlt', { organisation: e.organization_name })"
                class="size-5 rounded-full object-contain bg-white border border-gray-100"
                loading="lazy"
              />
              <span class="text-xs text-gray-500 truncate">{{ e.organization_name }}</span>
            </div>
            <h2 class="font-semibold text-gray-900 text-sm sm:text-base leading-snug line-clamp-2 group-hover:text-(--color-main) transition-colors">
              {{ e.title }}
            </h2>
            <div v-if="e.location_name" class="mt-2 text-xs text-gray-500 flex items-center gap-1 truncate">
              <UIcon name="i-lucide-map-pin" class="size-3 shrink-0" />
              <span class="truncate">{{ e.location_name }}</span>
            </div>
          </div>
        </NuxtLink>
      </div>

      <div v-else class="text-center py-16">
        <UIcon name="i-lucide-calendar-x" class="size-12 text-gray-300 mx-auto mb-4" />
        <p class="text-gray-500 mb-4">{{ t('vide.message', { ville: cityName }) }}</p>
        <NuxtLink to="/recherche/billetterie" class="text-sm text-(--color-main) hover:underline">
          {{ t('vide.voirTous') }}
        </NuxtLink>
      </div>

      <div v-if="data?.cities && data.cities.length > 1" class="mt-16 pt-12 border-t border-gray-100">
        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {{ t('autresVilles.titre') }}
        </h2>
        <p class="text-gray-500 text-sm mb-6">
          {{ t('autresVilles.sousTitre') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="city in data.cities.filter((c) => c.slug !== villeSlug).slice(0, 30)"
            :key="city.slug"
            :to="`/evenements/${city.slug}`"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 no-underline transition-colors"
          >
            <span>{{ city.name }}</span>
            <span class="text-gray-400">{{ city.count }}</span>
          </NuxtLink>
        </div>
      </div>

      <!-- Footer maillage interne SEO (discret, liens texte) -->
      <p class="mt-12 pt-6 border-t border-gray-100 text-sm text-gray-500 leading-relaxed">
        <span class="font-medium text-gray-700">{{ t('maillage.voirAussi', { ville: cityName }) }}</span>
        <NuxtLink :to="`/associations/ville/${villeSlug}`" class="text-(--color-main) hover:underline ml-1">{{ t('maillage.associations') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/cagnottes/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.cagnottes') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/adhesions/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.adhesions') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/boutiques/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.boutiques') }}</NuxtLink>
      </p>
      </UContainer>
    </div>
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/pages/evenements/[ville].json"></i18n>
